import Model from './model.js';
import Payroll from 'newModels/payroll.js';
import BankAccount from 'newModels/bank-account.js';
import Attachment from 'newModels/attachment.js';
import Attachments from 'newCollections/attachments.js';
import CompanyLocations from 'newCollections/company-locations.js';
import Payrolls from 'newCollections/payrolls.js';
import Employees from 'newCollections/employees.js';
import Integrations from 'newCollections/integrations.js';
import User from './user.js';
import axios from 'axios';
import moment from 'moment';
import config from 'config';

const FORMS_URL = config.apiUrl + `/GovernmentForms`;
const BULKUPLOADS_URL = config.apiUrl + `/BulkUploads`;

export default class Company extends Model {
    urlRoot() { return '/Companies'; }

    constraints() {
        const daysCalculationTypeConstraint = {
            numericality: {
                onlyInteger:          true,
                greaterThanOrEqualTo: 1,
                lessThanOrEqualTo:    3
            },
            presence: true
        };
        const fixedDaysConstraint = (calculationType) => ({
            numericality: {
                onlyInteger:          true,
                greaterThanOrEqualTo: 1,
                lessThanOrEqualTo:    31
            },
            presence: calculationType === 1
        });
        return {
            userId: {presence: true},
            companyName: {
                presence: {message: '^Company Name cannot be blank'}
            },
            type: {presence: true},
            eNumber: {
                format: {
                    pattern: /^\d{1,8}-\d{2}$/,
                    message: 'Invalid E Number format (Example: 99999999-99)'
                },
                length: {
                    is:      11,
                    message: '^The E Number must be 10 digits long and a hyphen'
                }
            },
            socsoRegNumber: {
                format: {
                    pattern: /^[a-zA-Z]([0-9]{10})[a-zA-Z]$/,
                    message:
                        'Invalid SOCSO Number format (Example: B3201002997A)'
                }
            },
            contactEmail: {presence: true, email: true},
            contactName: {presence: true},
            employeeCount: {
                numericality: {
                    onlyInteger:          true,
                    greaterThanOrEqualTo: 0
                }
            },
            annualLeaveDays: {numericality: true},
            hospitalizationLeaveDays: {numericality: true},
            maternityLeaveDays: {numericality: true},
            medicalLeaveDays: {numericality: true},
            marriageLeaveDays: {numericality: true},
            compassionateLeaveDays: {numericality: true},

            // <-- days calculation type -->
            prorateCalculationType: Object.assign(
                {},
                daysCalculationTypeConstraint),
            prorateFixedDays: (value, attrs) => fixedDaysConstraint(
                attrs.prorateCalculationType
            ),

            overtimeCalculationType: Object.assign(
                {},
                daysCalculationTypeConstraint),
            overtimeFixedDays: (value, attrs) => fixedDaysConstraint(
                attrs.overtimeCalculationType
            ),

            unpaidLeaveCalculationType: (value, attrs) =>
                attrs.recordUnpaidLeaveInPayroll === true ?
                    Object.assign({}, daysCalculationTypeConstraint) : null,
            unpaidLeaveFixedDays: (value, attrs) => fixedDaysConstraint(
                attrs.unpaidLeaveCalculationType
            ),
            // </- days calculation type -->

            reminderApproveLeaveEmailDays: {
                numericality: {
                    onlyInteger: true,
                    greaterThanOrEqualTo: 0
                }
            }
        };
    }

    initialize() {
        this.idAttribute = 'objectId';
        const r = super.initialize(...arguments);

        this.locations = new CompanyLocations(null, {
            company: this,
            pagination: false,
            syncRemove: true
        });
        this.payrolls = new Payrolls(null, {
            company: this,
            pagination: false,
            sortKey: 'periodDate',
            sortOrder: 'descending'
        });
        this.integrations = new Integrations(null, {
            company: this,
            pagination: false
        });

        return r;
    }

    save() {
        if (this.getBankAccount() && this.getBankAccount().dirty()) {
            return this.bankAccount.save()
                .then(() => this.set(
                    {bankAccountId: this.bankAccount.id},
                    {validate: false}
                ))
                .then(() => super.save(...arguments));
        }

        return super.save(...arguments);
    }

    destroy() {
        return axios.post(
            this.finalUrl() + '/destroy',
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => {
            this.trigger('destroy');
            return this;
        })
            .catch(err => {
                throw err.response.data.error;
            });
    }

    isSetUp() {
        return (
            this.get('isSetUp') || (
                this.isBasicInfoSetUp() &&
                this.get('regNumber') &&
                this.get('eNumber') &&
                this.get('epfRegNumber') &&
                this.get('socsoRegNumber')
            )
        );
    }

    isBasicInfoSetUp() {
        return (
            this.get('isSetUp') || (
                this.primaryLocation &&
                this.get('companyName') &&
                this.get('type') &&
                this.get('address')
            )
        );
    }

    fetchSubscription() {
        const user = new User({
            id: this.get('mppAdminId') || this.get('userId')
        });
        return user.fetchSubscription().then(subscription => {
            this.subscription = subscription;
            return subscription;
        });
    }

    getSubscription() {
        return this.subscription;
    }

    fetchBankAccount() {
        if (!this.get('bankAccountId')) {
            return Promise.resolve(null);
        }

        this.bankAccount = new BankAccount({
            objectId: this.get('bankAccountId')
        }, {validate: false});
        return this.bankAccount.fetch().then(() => this.bankAccount)
            .catch(() => {
                this.bankAccount = null;
                return this.bankAccount;
            });
    }

    getBankAccount() {
        return this.bankAccount;
    }

    setBankAccount(bankAccount) {
        this.bankAccount = bankAccount;
        this.set(
            {bankAccountId: bankAccount && bankAccount.id},
            {silent: true}
        );
    }

    fetchPrimaryLocation() {
        const locations = new CompanyLocations(null, {company: this});
        return locations.fetch(
            {filter: {where: {primary: true, hiddenAt: null}}}
        )
            .then(() => locations.at(0));
    }

    fetchLocations() {
        return this.locations.fetch(
            {filter: {where: {hiddenAt: null}}}
        ).then(() => this.locations);
    }

    fetchPayrolls() {
        return this.payrolls.fetch({filter: {
            where: {status: {neq: null}}
        }}).then(() => this.payrolls);
    }

    destroyUnsavedPayrolls() {
        return new Payrolls(null, {company: this, pagination: false})
            .fetch({filter: {where: {status: null}}, validate: false})
            .then(collection => {
                const payrolls = collection.remove(
                    collection.models, {validate: false}
                );
                return Promise.all(payrolls.map(payroll => {
                    if (payroll.id) {
                        return payroll.destroy();
                    }
                    return payroll;
                }));
            });
    }

    fetchUnsavedPayroll() {
        return new Payrolls(null, {company: this, pagination: false})
            .fetch({
                filter: {
                    where: {status: null},
                    order: '-createdAt',
                    limit: 1
                },
                validate: false
            })
            .then(collection => collection.at(0));
    }

    fetchCurrentPayroll() {
        return new Payrolls(null, {company: this, pagination: false})
            .fetch({filter: {
                where: {status: {lte: 2}},
                order: '-createdAt',
                limit: 1
            }})
            .then(collection => collection.at(0));
    }

    fetchLastSubmittedPayroll() {
        return new Payrolls(null, {company: this, pagination: false})
            .fetch({filter: {
                where: {status: {gte: 3}},
                order: '-createdAt',
                limit: 1
            }})
            .then(collection => collection.at(0));
    }

    fetchFormEAEmployees(year = moment().year() - 1) {
        return axios.get(
            this.finalUrl() + '/employees/formEA/',
            {
                params: {
                    year
                },
                headers: {'Authorization': app.user.getAccessToken().id}
            }
        ).then(({data}) => new Employees(data, {company: this}));
    }

    fetchPayableEmployees(payroll, rowsPerPage = null, forPeriod = null) {
        if (forPeriod) {
            forPeriod = moment(forPeriod).tz('Asia/Kuala_Lumpur')
                .endOf('month')
                .toDate();
        }
        const period = forPeriod || (payroll || new Payroll())
            .getPeriod()
            .endOf('month')
            .toDate();

        if (payroll && payroll.get('revertedAt')) {
            if (payroll.get('payslips') <= 0) {
                return new Employees(null, {company: this, pagination: false});
            }

            return payroll.fetchEmployees();
        }

        const query = {filter: {
            where: {
                or: [
                    {
                        and: [
                            {commencementDate: {neq: null}},
                            {commencementDate: {lte: period}}
                        ],
                        hiddenAt: null
                    },
                    {
                        and: [
                            {commencementDate: {neq: null}},
                            {commencementDate: {lte: period}},
                            {cessationDate: {neq: null}},
                            {cessationDate: {
                                gte:
                                    moment(period)
                                        .startOf('month')
                                        .toDate()
                            }}
                        ]
                    }
                ]
            }
        }};

        const pagination = rowsPerPage ?
            {rowsPerPage} :
            false;

        if (!rowsPerPage) {
            query.filter.limit = 9999;
        }

        return new Employees(null, {
            company: this,
            pagination
        }).fetch(query);
    }

    fetchAttachments(tags = []) {
        const attachments = new Attachments(null, {attachable: this});

        return attachments.fetch({filter: {
            where: {
                tags: {inq: tags}
            },
            limit: 20
        }});
    }

    fetchIntegration() {
        return this.integrations.fetch({filter: {
            where: {
                or: [
                    {expiresAt: {gt: new Date()}},
                    {renewAt: {gt: new Date()}}
                ],
                hiddenAt: null
            },
            order: '-createdAt',
            limit: 1
        }}).then(() => this.integrations.at(0));
    }

    getIntegration() {
        return this.integrations.at(0);
    }

    getCompanyRegNumbers() {
        return {
            eNumber: 'E Number',
            regNumber: 'Company Registration Number',
            epfRegNumber: 'EPF Number',
            socsoRegNumber: 'SOCSO Number'
        };
    }

    getMissingRegNumbers() {
        const res = [];
        Object.keys(this.getCompanyRegNumbers()).forEach(attr => {
            const label = this.getCompanyRegNumbers()[attr];
            if (!this.get(attr)) {
                res.push(label);
            }
        });
        return res;
    }

    sendFormEA(
        employeeIds,
        period = moment().year() - 1,
        ccEmail = ''
    ) {
        return axios.post(
            `${FORMS_URL}/${this.id}/sendFormEA`,
            {employeeIds, period, ccEmail},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this)
            .catch(err => {
                throw err.response.data.error;
            });
    }

    generateFormE(period = moment().year() - 1) {
        return axios.post(
            `${FORMS_URL}/${this.id}/generateFormE`,
            {period},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    generateFormCp8d(format = 'txt', period = moment().year() - 1) {
        return axios.post(
            `${FORMS_URL}/${this.id}/generateFormCp8d`,
            {period, format},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    getLeaveDays(type) {
        const days = this.get('leaveDays') || {};
        return parseFloat(days[type.id]) || 0;
    }

    setLeaveDays(options) {
        const leaveDays = this.get('leaveDays') || {};
        const {type, days} = options;
        let {typeId} = options;

        if (!typeId) {
            typeId = type.id;
        }
        leaveDays[typeId] = parseFloat(days || 0);
        this.set('leaveDays', leaveDays);
    }

    setMPPAdmin(mppAdminId) {
        return axios.post(
            this.finalUrl() + '/setMPPAdmin',
            {userId: mppAdminId},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch({validate: false}))
            .catch(err => {
                throw err.response.data.error;
            });
    }

    unsetMPPAdmin() {
        return axios.post(
            this.finalUrl() + '/unsetMPPAdmin',
            {},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch({validate: false}))
            .catch(err => {
                throw err.response.data.error;
            });
    }

    uploadLogo(file = null) {
        if (!file) {
            return Promise.resolve();
        }

        // Size limit: 200 kb
        if (file && file.size && file.size > 1024 * 50) {
            return Promise.reject({text: `The maximum allowed size is 50kb`});
        }

        if (!/^image\/(png|jpeg)$/.test(file.type)) {
            return Promise.reject({
                text: `Only PNG or JPEG images are allowed`
            });
        }

        const extension = file.name.split('.').pop();
        const data = new FormData();
        data.append(`file`, file, `company-logo.${extension}`);

        // Hack to cover `unsavedAttributes` issue
        const pendingChanges = Object.assign({}, this.unsavedAttributes || {});

        return axios.post(
            `${this.finalUrl()}/logo`,
            data,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch({validate: false})
        ).then(() => {
            // tracking of `unsavedAtrributes` is working incorrectly as
            // backbone appends server sent attributes into model without reset
            this.unsavedAttributes = Object.assign(
                this.unsavedAttributes || {},
                pendingChanges
            );
            this.set(pendingChanges);
        });
    }

    unsetLogo() {
        return axios.delete(`${this.finalUrl()}/logo`, {
            headers: {'Authorization': app.user.getAccessToken().id}
        });
    }

    getLogoUrl() {
        if (!this.get('logoUrl')) {
            return false;
        }

        return `${this.finalUrl()}/logo?` +
            `access_token=${app.user.getAccessToken().id}&` +
            `_b=${Math.floor(Math.random() * 1000000)}`;
    }

    payslipsTemplateDownloadUrl() {
        return `${BULKUPLOADS_URL}/downloadPayslipsTemplate/${this.id}?` +
            `access_token=${app.user.getAccessToken().id}`;
    }

    bulkEmployeesTemplateDownloadUrl() {
        return `${BULKUPLOADS_URL}/downloadBulkEmployeesTemplate/${this.id}?` +
            `access_token=${app.user.getAccessToken().id}`;
    }

    timesheetsTemplateDownloadUrl() {
        return `${BULKUPLOADS_URL}/downloadTimesheetsTemplate/${this.id}?` +
            `access_token=${app.user.getAccessToken().id}`;
    }

    employeeDataUrl() {
        return `${this.finalUrl()}/employee-data?` +
            `access_token=${app.user.getAccessToken().id}`;
    }

    generateLHDNAuditFiles(year = moment().year() - 1) {
        return axios.post(
            `${this.finalUrl()}/audit-zip`,
            {year},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    setMaxEmployeeCount(count = 0) {
        return axios.post(
            `${this.finalUrl()}/maxEmployeeCount`,
            {count},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then((res) => {
            return new Attachment(res.data.result);
        });
    }

    isAllowAddEmployees() {
        return (this.get('employeeCount') || 0) <
            (this.get('maxEmployeeCount') || config.defaultMaxEmployeeCount);
    }
}



// WEBPACK FOOTER //
// ./app/new-models/company.js